// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

// Balance-panel presentation model (COB-354, generalised estate-wide in
// COB-355). Pure helpers shared by every money surface — the hot wallet,
// the yield vault, and the shared multisig vault — so the "lead with
// spendable" rule lives in exactly one place.

/** The two figures a balance panel renders. `headlineSats` is the
 *  *spendable* balance to lead with; `pendingSats` is the still-confirming
 *  surplus, or `null` when there is none to surface. */
export interface BalancePanelModel {
  /** The spendable (confirmed) figure to headline, or `null` when unavailable. */
  headlineSats: number | null;
  /** The confirmed+unconfirmed surplus still awaiting confirmation, or `null`. */
  pendingSats: number | null;
}

/** The raw confirmed/total figures a balance panel is derived from. Kept
 *  structural (not tied to any one response type) so the wallet — which
 *  sums its own UTXOs — and the shared-vault detail response can both feed
 *  the same helper. Both fields are optional/nullable: absent means the
 *  figure is unavailable (server lookup failed, or not yet loaded). */
export interface BalanceFigures {
  /** Confirmed + unconfirmed balance at the address, in sats. */
  balance_sats?: number | null;
  /** The confirmed-only (spendable) subtotal of `balance_sats`. */
  confirmed_balance_sats?: number | null;
}

/** COB-354: the balance panel leads with what can actually be spent. The
 *  withdrawal builder spends confirmed UTXOs only (a create over unconfirmed
 *  change 409s until it confirms), so the headline is the `confirmed_balance_sats`
 *  subtotal — falling back to the confirmed+unconfirmed `balance_sats` total only
 *  when the confirmed figure is absent, and to `null` ("unavailable") when both
 *  are. `pendingSats` is the still-confirming remainder (total − confirmed),
 *  surfaced only when the total genuinely exceeds the spendable figure — so the
 *  panel never headlines a number the only available action cannot yet use. */
export function balancePanelModel(figures: BalanceFigures): BalancePanelModel {
  const total = figures.balance_sats ?? null;
  const confirmed = figures.confirmed_balance_sats ?? null;
  const headlineSats = confirmed ?? total;
  const pendingSats =
    total != null && confirmed != null && total > confirmed ? total - confirmed : null;
  return { headlineSats, pendingSats };
}

/** COB-354: the send-section hint shown when an ACTIVE vault's balance is *all*
 *  still confirming — the confirmed (spendable) subtotal is exactly zero while
 *  the total is positive (a fresh deposit, or withdrawal change returning to the
 *  vault address, that has not had its first confirmation). The withdrawal
 *  builder spends confirmed UTXOs only, so a create would 409 until they
 *  confirm; this line explains the wait rather than letting the (server-gated)
 *  Create button read as broken. `null` (no hint) whenever there is spendable
 *  balance, no balance at all, the balance figures are unavailable, or the vault
 *  is not ACTIVE. */
export function awaitingConfirmationHint(
  vault: BalanceFigures & { state: string },
): string | null {
  if (vault.state !== 'ACTIVE') return null;
  const total = vault.balance_sats ?? null;
  const confirmed = vault.confirmed_balance_sats ?? null;
  if (confirmed === 0 && total != null && total > 0) {
    return 'Funds from a recent deposit or withdrawal change are still confirming — you can send once they confirm (typically one block).';
  }
  return null;
}
